import { Dispatch, useState } from "react";
import { Sala, integrante, toSala, Ranking } from "../../modelos/Sala";
import { useBaseDeDatos } from "../../generalUtils/repositoryUtils/useBaseDeDatos";
import { firestore } from "../../firebase";
import { DocumentData, DocumentSnapshot, QuerySnapshot, Timestamp, collection, doc, getCountFromServer, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { nomJuegoIntegrantes, nomJuegoSimultaneo, nomProgMemNombres, nomProgMemNumeros, nomProgMemPalabras, nomProgParejas, nomProgSumas1, nomProgSuperCerebrosDice } from "../../datos/nombresBD";
import { aVersusUsuario, mensaje, toVersus } from "../../modelos/Versus";
import { aIntegrante, toIntegrante } from "../../modelos/entidad/IntegranteEntidad";

export const useSala = () => {

    /**
     * Funciones para acceder a la base de datos
     * @type {Objecto}
     * @const
     */
    const {
        agregarDoc,
        borrarDoc,
        reemplazarDoc,
        recogerDoc,
        agregarCollection,
        recogerDocsFiltrados,
        borrarCollection,
        recogerDocs,
        UnirCampoArregloDoc,
        EliminarCampoArregloDoc,
        IncrementarCampo,
        ActualizarUnCampo,
    } = useBaseDeDatos();

    function generarID(length: number) {
        var caracteres = '0123456789';
        var id = '';
        for (var i = 0; i < length; i++) {
          id += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
        }
        return id;
      }

    const rutaSala = (idSala:string) => `${nomJuegoSimultaneo}/${idSala}`;
    const rutaJugadores = (idSala:string) => `${nomJuegoSimultaneo}/${idSala}/jugadores`;
    const rutaRanking = (idSala:string) => `${nomJuegoSimultaneo}/${idSala}Resultados/Ranking`;

    /**
     * Borra todos los documentos de una coleccion (en paralelo).
     * Se usa para limpiar salas recicladas: los IDs son de 4 digitos y se
     * reutilizan, y las subcolecciones no se borran al borrar el documento padre.
     */
    const limpiarColeccion = async (ruta:string) => {
        try{
            const docs = await recogerDocs(ruta);
            await Promise.all(docs.map(d => borrarDoc(`${ruta}/${d.id}`)));
        }catch(e){
            console.error("Error limpiando coleccion", ruta, e);
        }
    }

    const SubirResultados = async (total:number,ultimoResultado:number, tiempo: Timestamp, nombre:string, telefono:string, idSala:string,indiceJuego:number): Promise<boolean>=> {
        const dato = {
            nombre:nombre,
            telefono:telefono,
            indiceJuego:indiceJuego,
            total:total,
            tiempo:tiempo,
            ultimoResultado,
        } as Ranking
        // Reintentos con espera: si la red falla justo al terminar el juego,
        // el resultado no se pierde.
        for(let intento = 0; intento < 3; intento++){
            try{
                await agregarDoc(`${rutaRanking(idSala)}/${telefono}`, dato);
                return true;
            }catch(e){
                console.error(`Error subiendo resultado (intento ${intento+1})`, e);
                await new Promise(res => setTimeout(res, 1500 * (intento+1)));
            }
        }
        return false;
    }

    const crearSala = async (nomJuego: string[]): Promise<string> => {
        try {
            let idPersonalizado: string;
            let docSala: DocumentSnapshot<DocumentData>;
            let docResultados: DocumentSnapshot<DocumentData>;
            do{
                idPersonalizado = generarID(4);
                [docSala, docResultados] = await Promise.all([
                    recogerDoc(rutaSala(idPersonalizado)),
                    // Un torneo guardado con GuardarTorneo ocupa el ID: no
                    // reutilizarlo para no borrar sus resultados historicos.
                    recogerDoc(`${nomJuegoSimultaneo}/${idPersonalizado}Resultados`),
                ]);
            }while(docSala.exists() || docResultados.exists());

            // Limpiar residuos huerfanos de una sala anterior que haya usado el
            // mismo ID (jugadores y resultados viejos danan el contador y el ranking).
            await Promise.all([
                limpiarColeccion(rutaJugadores(idPersonalizado)),
                limpiarColeccion(rutaRanking(idPersonalizado)),
            ]);

            const sala = { iniciar: 0, juego: nomJuego, abierto: true };
            await agregarDoc(rutaSala(idPersonalizado), sala);
            return idPersonalizado;
        } catch (error) {
            console.error("Error al crear la sala:", error);
            throw error; // Propaga el error hacia arriba
        }
    }

    const RecogerSala = async (sala: string) => {
        const doc = await recogerDoc(`${nomJuegoSimultaneo}/${sala}`)
        return toSala(doc);
    }

    /**
     * Registra un jugador en la sala escribiendo SU PROPIO documento en la
     * subcoleccion "jugadores". Cada jugador escribe un documento distinto,
     * asi que no hay contencion aunque entren cientos al mismo tiempo
     * (el modelo anterior hacia arrayUnion sobre un unico documento y las
     * escrituras se pisaban con muchos ingresos simultaneos).
     */
    // Distingue el motivo del rechazo: la UI necesita mostrar un mensaje
    // distinto para "no existe", "torneo ya iniciado" y "error de red/permisos"
    // (antes los tres casos devolvian el mismo `false` y el jugador siempre
    // veia "No existe la sala", incluso cuando el problema era, por ejemplo,
    // que las reglas de Firestore no cubrian la subcoleccion "jugadores").
    const nuevoJugador = async(idSala:string,telefono:string,nombre:string):Promise<'ok'|'no_existe'|'cerrada'|'error'> => {
        try{
            const docSala = await recogerDoc(rutaSala(idSala));
            if(!docSala.exists()) return 'no_existe';
            const sala = toSala(docSala);
            if(sala.abierto === false){
                // Sala cerrada: solo puede reingresar quien ya estaba registrado.
                const docJugador = await recogerDoc(`${rutaJugadores(idSala)}/${telefono}`);
                return docJugador.exists() ? 'ok' : 'cerrada';
            }
            await agregarDoc(`${rutaJugadores(idSala)}/${telefono}`, {
                nombre,
                telefono,
                ingreso: serverTimestamp(),
            });
            return 'ok';
        }catch(e){
            console.error("Error registrando jugador:", e);
            return 'error';
        }
    }

    const iniciarJuego = async(idSala : string) => {
        // Solo marca la sala como cerrada; nunca reescribe la lista de jugadores.
        await ActualizarUnCampo(rutaSala(idSala), 'abierto', false);
    }

    const iniciarSiguienteJuego = async (idSala : string) => {
        // Incremento atomico en el servidor: no hay carrera de leer-modificar-escribir.
        await IncrementarCampo(rutaSala(idSala), 'iniciar', 1);
    }

    /** Borra la sala completa: jugadores y documento principal. */
    const cerrarSalaColeccion = async(idSala:string) =>{
        await limpiarColeccion(rutaJugadores(idSala));
        await borrarDoc(rutaSala(idSala));
    }

    /** Borra solo el documento de la sala: es la senal que manda a los jugadores a la pantalla final. */
    const cerrarSalaDoc = async(idSala:string) =>{
        try{
            await borrarDoc(rutaSala(idSala));
        }catch(e){
            console.error("Error cerrando sala:", e);
        }
    }

    const subscribirDoc = (ruta:string,setData:Dispatch<any>,setListening?:Dispatch<any>) => {
        const documentRef = doc(firestore,ruta);
        const unsubscribe = onSnapshot(documentRef,(doc) => {
            setData(
                toSala(doc)
            )
        },(error) => {
            console.error("Error en la suscripcion al documento", ruta, error);
        });

        // Compatibilidad: algunos llamadores guardan la funcion unsubscribe en estado
        if(setListening) setListening(() => unsubscribe);
        return unsubscribe;
    }

    /**
     * Suscripcion al numero de jugadores de la sala. Solo entrega el conteo,
     * no los datos, para mantener liviano el render del host.
     */
    const SubscribirJugadores = (idSala:string,setCantidad:Dispatch<number>) => {
        const jugadoresRef = collection(firestore, rutaJugadores(idSala));
        const unsubscribe = onSnapshot(jugadoresRef,(snapshot: QuerySnapshot) => {
            setCantidad(snapshot.size);
        },(error) => {
            console.error("Error en la suscripcion a jugadores de la sala", idSala, error);
        });
        return unsubscribe;
    }

    /** Conteo puntual de jugadores usando agregacion del servidor (1 lectura). */
    const ContarJugadores = async (idSala:string): Promise<number> => {
        const jugadoresRef = collection(firestore, rutaJugadores(idSala));
        const snapshot = await getCountFromServer(query(jugadoresRef));
        return snapshot.data().count;
    }

    const desubscribir = (listening:any, setListening:Dispatch<any>,setData:Dispatch<any>) => {
        console.log('Empieza a realizar la funcion de desubscripcion.')
        if (listening) {
            console.log('zzzzz la funcion de desubscripcion.',listening)
            // Llamar a la función unsubscribe para dejar de escuchar cambios
            listening();
            setListening(null);
            setData(null);
        }
    }

    const TraerRankings = async (idSala:string): Promise<Ranking[]> => {
        const documentRef = collection(firestore,rutaRanking(idSala));
        const q = query(documentRef, orderBy("total","desc"), orderBy("tiempo","asc"));
        const querySnapshot  = await getDocs(q)
        return querySnapshot.docs.map(doc => doc.data() as Ranking);
    }

    /**
     * Suscripcion al ranking completo, ordenado por total y tiempo.
     * Sin limit: el host la usa para contar cuantos terminaron el juego
     * actual, asi que necesita ver todos los resultados (el limit(10)
     * anterior era la causa del contador pegado con mas de 10 jugadores).
     */
    const SubscribirRankings = (idSala:string,setData:Dispatch<Ranking[]>,setListening?:Dispatch<any>) => {
        const documentRef = collection(firestore,rutaRanking(idSala));
        const q = query(documentRef, orderBy("total","desc"), orderBy("tiempo","asc"));

        const unsubscribe = onSnapshot(q,(snapshot: QuerySnapshot) => {
            setData(snapshot.docs.map(doc=>doc.data() as Ranking))
        },(error) => {
            // Tipico: falta el indice compuesto (total desc, tiempo asc) o reglas de seguridad.
            console.error("Error en la suscripcion al ranking de la sala", idSala, error);
        });

        if(setListening) setListening(() => unsubscribe);
        return unsubscribe;
    }

    const GuardarTorneo = async (idSala:string,nombre:string) => {
        const ruta = nomJuegoSimultaneo + '/' + `${idSala}Resultados`
        await agregarDoc(ruta,{nombre:nombre} as Object)
    }

    const BorrarResultados = async (idSala:string) => {
        // Borrar tambien los documentos del ranking: quedar residuos con el
        // mismo ID de sala contamina el contador de un torneo futuro.
        await limpiarColeccion(rutaRanking(idSala));
        await borrarDoc(`${nomJuegoSimultaneo}/${idSala}Resultados`)
    }

    const TraerResultado = async (telefono:string,idSala:string): Promise<Ranking | null> => {
        try{
            let doc = await recogerDoc(`${rutaRanking(idSala)}/${telefono}`)
            if(doc.exists()){
                return doc.data() as unknown as Ranking
            }
            return null
        }catch(e){
            console.error("Error trayendo resultado previo:", e);
            return null
        }
    }
    // Suscripcion para un versus 1v1

    const BuscarVersus = (ruta:string,setVersusAbiertos:Dispatch<any>,setListening:Dispatch<any>) => {
        
        subscribirDoc(ruta, (input) => {
            setVersusAbiertos(input?.integrantes.length >= 1 ? input?.integrantes : []);
            
        }, setListening);
    }

    const CrearVersus = async (idPersonalizado:string,uid:string) => {        
        try {
            // Verificar si el documento ya existe en la colección
            const doc = await recogerDoc( nomJuegoSimultaneo + '/' + "versus" + "/" + 'salas' + "/" + idPersonalizado);
            console.log("🚀 ~ CrearVersus ~ idPersonalizado:", idPersonalizado)
            console.log("🚀 ~ CrearVersus ~ CrearVersus:",doc)
            console.log("🚀 ~ CrearVersus ~ doc.exists():", doc.exists())
            
            if (doc.exists()) {
                const versus = toVersus(doc)
                console.log("🚀 ~ CrearVersus ~ versus update:", versus)
                if(versus.integrantes.length > 1){
                    console.log("🚀 ~ CrearVersus ~ versus:", versus)
                    throw new Error("Error integrantes completos.");
                }else{
                    versus.integrantes.push({uid} as integrante)
                    await UnirCampoArregloDoc(nomJuegoSimultaneo + '/' + "versus" + "/" + 'salas' + "/" + idPersonalizado,'integrantes',{uid} as integrante)
                    await BorrarJugadorVersus(uid)
                }                
            } else {
                // Si el documento no existe, crea una nueva sala
                let int = [{uid} as integrante]
                let versus = aVersusUsuario(0,int,null,[])
                console.log("🚀 ~ CrearVersus ~ versus crear:", versus)
                await agregarDoc(nomJuegoSimultaneo + '/' + "versus" + "/" + 'salas' + "/" + idPersonalizado, versus)
                await BorrarJugadorVersus(uid)
                const respuesta = idPersonalizado
                return respuesta;
            }
        } catch (error) {
            throw new Error("Error al crear la sala:", error);
        }
    }

    const AgregarJugadorVersus = async (nombre:string,uid:string) => {
        let doc = await recogerDoc(nomJuegoSimultaneo + "/versus");
        let versus = toVersus(doc);

        if (!versus) {
            // Si el documento aún no existe, crea un nuevo objeto versus
            versus = {
                integrantes: [{ nombre, uid, pareja: [], aceptado: null }]
            };
            console.log("🚀 ~ AgregarJugadorVersus ~ versus:", versus)
            await agregarDoc(nomJuegoSimultaneo + "/versus", versus);
            console.log("Se creó el documento versus con el integrante nuevo.");
            return true
        } else {
            // Si el documento ya existe
            if (!versus.integrantes) {
                // Si el campo integrantes no existe, inicialízalo como un array vacío
                versus.integrantes = [];
            }
            
            // Verificar si el nuevo integrante ya está presente en la lista de integrantes
            const integranteRepetido = versus.integrantes.find(integrante => integrante.uid === uid);
            
            if (!integranteRepetido) {
                // Si el integrante no está repetido, se añade a la lista
                versus.integrantes.push({ nombre, uid, pareja: null, aceptado: null });
                console.log("🚀 ~ AgregarJugadorVersus ~ versus:", versus)
                await agregarDoc(nomJuegoSimultaneo + "/versus", versus);
                console.log("El integrante ahora está presente en la lista de integrantes.");
                return true
            } else {
                // Si el integrante ya está presente, puedes manejar esta situación según tus necesidades
                console.log("El integrante ya está presente en la lista de integrantes.");
                return false
            }
        }
    }

    const EnviarInvitacionVersus = async (nombre: string, uid: string) => {
        try {
            // Recoger el documento del versus de la base de datos
            let docVersus = await recogerDoc(`${nomJuegoSimultaneo}/versus`);
            let versus = toVersus(docVersus);
    
            // Verificar si el documento existe y tiene la propiedad integrantes
            if (versus && versus.integrantes) {
                // Encontrar el usuario con el uid proporcionado
                const usuario = versus.integrantes.find((usuario: integrante) => usuario.uid === uid);
                
                // Verificar si se encontró el usuario
                if (usuario) {
                    // Verificar si el nombre ya existe en el array usuario.pareja
                    if (!usuario.pareja?.includes(nombre)) {
                        // Agregar el atributo nombre de myUsuario al atributo pareja del usuario encontrado
                        if (!usuario.pareja) {
                            usuario.pareja = [];
                        }
                        usuario.pareja.push(nombre);
    
                        // Actualizar el documento en la base de datos
                        console.log("🚀 ~ EnviarInvitacionVersus ~ versus:", versus)
                        await agregarDoc(`${nomJuegoSimultaneo}/versus`, versus);
    
                        console.log(`Se agregó ${nombre} a la pareja del usuario con uid ${uid}.`);
                    } else {
                        console.log(`${nombre} ya existe en la pareja del usuario con uid ${uid}.`);
                    }
                } else {
                    console.log(`No se encontró ningún usuario con uid ${uid}.`);
                }
            } else {
                console.log("No se encontró el documento versus o no tiene la propiedad integrantes.");
            }
        } catch (error) {
            console.error("Error al enviar la invitación:", error);
        }
    };

    const GenerarID = async (canti:number) => {
        let doc = null;
        let idPersonalizado = null;
        do{
            idPersonalizado = generarID(canti);
            doc = await recogerDoc( nomJuegoSimultaneo + '/' + "versus" + "/" + 'salas' + "/" + idPersonalizado);
        } while(doc.exists())
        console.log("🚀 ~ constEnviarAceptación= ~ idPersonalizado:", idPersonalizado)
        return idPersonalizado
    }

    const CambiarAceptado = async(nombre:string,idPersonalizado:string,currentUid:integrante) => {
        // Recoger el documento del versus de la base de datos
        let docVersus = await recogerDoc(`${nomJuegoSimultaneo}/versus`);
        let versus = toVersus(docVersus);
        
        // Verificar si el documento existe y tiene la propiedad integrantes
        if (versus && versus.integrantes) {
            const usuario = versus.integrantes.find((usuario: integrante) => usuario.nombre === nombre);
            if(usuario.aceptado === null){
                usuario.aceptado = idPersonalizado;
                await agregarDoc(`${nomJuegoSimultaneo}/versus`, versus);
                return true
            }else{
                await EliminarInvitacion(nombre,currentUid)
                console.log(`No se encontró ningún usuario con nombre ${nombre} o ya tiene algo en el atributo aceptado.`);
                return false
            }
            
        } else {
            console.log("No se encontró el documento versus o no tiene la propiedad integrantes.");
            return false
        }
    }

    const ModificarActualUsuario = async(currentUid: integrante,idPersonalizado:string) => {
        // Recoger el documento del versus de la base de datos
        let docVersus = await recogerDoc(`${nomJuegoSimultaneo}/versus`);
        let versus = toVersus(docVersus);
        const actualUser = versus.integrantes.find((otroUsuario: integrante) => otroUsuario.uid === currentUid.uid);
        if(actualUser.aceptado === null){
            actualUser.aceptado = idPersonalizado;
            await agregarDoc(`${nomJuegoSimultaneo}/versus`, versus);
            return true
        }else{
            console.log("El otroUsuario ya tenia aceptado.");
            return false

        }

    }

    const EnviarAceptación = async (nombre: string, currentUid: integrante) => {
        try {
            let idPersonalizado = await GenerarID(6)
            if(CambiarAceptado(nombre,idPersonalizado,currentUid)){
                if(ModificarActualUsuario(currentUid,idPersonalizado)){
                    let docVersus = await recogerDoc(`${nomJuegoSimultaneo}/versus`);
                    let versus = toVersus(docVersus);
                    const otroUser = versus.integrantes.find((otroUsuario: integrante) => otroUsuario.nombre === nombre);
                    const actualUser = versus.integrantes.find((otroUsuario: integrante) => otroUsuario.uid === currentUid.uid);
                    if(otroUser.aceptado === actualUser.aceptado){
                        console.log("🚀 ~ constEnviarAceptación= ~ otroUser.aceptado === actualUser.aceptado:", otroUser.aceptado === actualUser.aceptado)
                        await CrearVersus(idPersonalizado,currentUid.uid)
                    }
                }
            }
            return idPersonalizado       
        } catch (error) {
            console.error("Error al enviar la aceptación:", error);
            return false
        }
    };

    const EliminarInvitacion = async (nombre: string, currentUid: integrante) => {
        let newUser = currentUid.pareja.filter(par => par !== nombre)
        console.log("🚀 ~ EliminarInvitacion ~ newUser:", newUser)
        console.log("🚀 ~ EliminarInvitacion ~ currentUid:", currentUid)
        await EliminarCampoArregloDoc(nomJuegoSimultaneo + "/versus",'integrantes',currentUid)
        await UnirCampoArregloDoc(nomJuegoSimultaneo + "/versus",'integrantes',newUser)
    }

    const BorrarJugadorVersus = async (uid: string) => {
        // Recoger el documento actual de la base de datos
        let doc = await recogerDoc(nomJuegoSimultaneo + "/versus");
        let versus = toVersus(doc);
    
        // Filtrar los integrantes para eliminar el integrante con el uid proporcionado
        versus.integrantes = versus?.integrantes?.filter(integrante => integrante.uid !== uid);
    
        // Actualizar el documento en la base de datos
        await reemplazarDoc(nomJuegoSimultaneo + "/versus", versus);
    };

    const EnviarMensaje = async (idSala:string,nombre:string,mensaje:string) => {
        let currentMensaje = {nombre,mensaje} as mensaje;
        await UnirCampoArregloDoc(nomJuegoSimultaneo + "/versus/salas/" + idSala,'mensaje',currentMensaje)
    }
    const EnviarReady = async (idSala: string, cant: number) => {
        let juegos = [nomProgMemNombres, nomProgMemPalabras, nomProgSumas1, nomProgSuperCerebrosDice, nomProgParejas];
        let indexAzar = Math.floor(Math.random() * juegos.length);
        
        // Incrementa el campo
        await IncrementarCampo(nomJuegoSimultaneo + "/versus/salas/" + idSala, 'iniciar', cant);
        
        // Recoge el documento
        let doc = await recogerDoc(nomJuegoSimultaneo + "/versus/salas/" + idSala);
        
        // Convierte el documento a un objeto 'versus'
        let versus = toVersus(doc);
        
        // Verifica si versus.juego es null y actualiza si es necesario
        if (versus.juego === null || versus.juego === undefined) {
            await ActualizarUnCampo(nomJuegoSimultaneo + "/versus/salas/" + idSala, 'juego', juegos[indexAzar]);
        }
    }
    
    const GuardarPuntaje = async (idSala:string,uid:string,nombre:string,puntaje:number) => {
        let registro = {uid,nombre,puntaje} as integrante
        console.log("🚀 ~ GuardarPuntaje ~ registro:", registro)
        await EliminarCampoArregloDoc(nomJuegoSimultaneo + "/versus/salas/" + idSala,'integrantes',uid)
        await UnirCampoArregloDoc(nomJuegoSimultaneo + "/versus/salas/" + idSala,'integrantes',registro)
    }

    const BorrarSala = (idSala:string) => {
        borrarDoc(nomJuegoSimultaneo + "/versus/salas/" + idSala)
    }

    return {
        crearSala,
        cerrarSalaColeccion,
        cerrarSalaDoc,
        nuevoJugador,
        subscribirDoc,
        SubscribirJugadores,
        ContarJugadores,
        desubscribir,
        iniciarJuego,
        iniciarSiguienteJuego,
        SubirResultados,
        TraerRankings,
        TraerResultado,
        SubscribirRankings,
        RecogerSala,
        GuardarTorneo,
        BorrarResultados,
        BuscarVersus,
        CrearVersus,
        AgregarJugadorVersus,
        BorrarJugadorVersus,
        EnviarInvitacionVersus,
        EnviarAceptación,
        EnviarMensaje,
        EnviarReady,
        GuardarPuntaje,
        BorrarSala,
    }
}
